import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { contractService, AnalysisProgress } from '../../../../services/api/contractService';
import { Contract, ContractAnalysisJSON } from '../../../../types/contract.types';
import {
  WorkflowState,
  ClauseDecision,
  ClauseDecisionStatus
} from '../../../../types/workflow.types';

interface AnalysisVersionInfo {
  _id: string;
  version: number;
  fileName: string;
  analysisDate: string;
  createdAt: string;
}

interface UseContractWorkflowState {
  contract: Contract | null;
  analysis: ContractAnalysisJSON | null;
  analysisId: string | null; // Track the analysis ID for clause decisions
  analysisVersion: number; // Current analysis version number
  analysisVersions: AnalysisVersionInfo[]; // All available versions
  clauseDecisions: Record<string, ClauseDecision>;
  selectedForNegotiation: string[];
  workflowState: WorkflowState | null;
  currentStep: number;
  completedSteps: number[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface UseContractWorkflowReturn extends UseContractWorkflowState {
  setCurrentStep: (step: number) => void;
  saveProgress: (updates: Partial<UseContractWorkflowState>) => Promise<void>;
  canProceedToStep: (stepNumber: number) => boolean;
  updateClauseDecision: (clauseId: string, decision: Partial<ClauseDecision>) => Promise<void>;
  bulkUpdateClauseDecisions: (clauseIds: string[], status: ClauseDecisionStatus) => Promise<void>;
  toggleNegotiationSelection: (clauseId: string) => void;
  setSelectedForNegotiation: (clauseIds: string[]) => void;
  refreshContract: () => Promise<void>;
  refreshAnalysis: () => Promise<void>;
  runAnalysis: (onProgress?: (progress: AnalysisProgress) => void, forceRefresh?: boolean, model?: string, provider?: 'anthropic' | 'openai') => Promise<void>;
  selectAnalysisVersion: (version: number) => Promise<void>;
  getStepProgress: () => { reviewed: number; total: number; percentage: number };
  completeReview: (emailData?: { subject: string; body: string }) => Promise<void>;
}

const initialWorkflowState: WorkflowState = {
  currentStep: 1,
  completedSteps: [],
  stepProgress: {
    creation: { status: 'not_started' },
    analysis: { status: 'not_started' },
    clauseReview: { status: 'not_started', reviewedCount: 0, totalCount: 0 },
    summary: { status: 'not_started' },
    negotiation: { status: 'not_started', emailsSent: 0 }
  },
  lastModified: new Date(),
  lastModifiedBy: ''
};

export function useContractWorkflow(contractId: string | undefined): UseContractWorkflowReturn {
  const { token, user } = useAuth();

  const [state, setState] = useState<UseContractWorkflowState>({
    contract: null,
    analysis: null,
    analysisId: null,
    analysisVersion: 1,
    analysisVersions: [],
    clauseDecisions: {},
    selectedForNegotiation: [],
    workflowState: null,
    currentStep: 1,
    completedSteps: [],
    isLoading: true,
    isSaving: false,
    error: null,
  });

  // Fetch contract data
  const fetchContract = useCallback(async () => {
    if (!contractId || !token) return null;

    try {
      const contractData = await contractService.getById(token, contractId);

      // Map the API response to our Contract type
      const contract: Contract = {
        id: contractData._id || contractData.id?.toString() || '',
        _id: contractData._id,
        contractTitle: contractData.contractTitle,
        supplier: contractData.supplier?.companyName || contractData.supplier?.name || 'Unknown Supplier',
        supplierId: contractData.supplierId,
        startDate: contractData.startDate,
        endDate: contractData.endDate,
        value: contractData.value || 0,
        status: contractData.status,
        reviewStatus: contractData.reviewStatus,
        category: contractData.description || 'General',
        description: contractData.description,
        paymentTerms: contractData.paymentTerms,
        paymentCurrency: contractData.paymentCurrency || contractData.currency,
        minPurchaseAmount: contractData.minPurchaseAmount,
        marketingBudget: contractData.marketingBudget,
        renewalTermsCount: contractData.renewalTermsCount,
        incotermTerms: contractData.incotermTerms,
        lastModified: contractData.updatedAt,
        createdBy: contractData.createdBy,
        // Include negotiation email if available
        negotiationEmail: contractData.negotiationEmail,
      };

      return contract;
    } catch (error) {
      console.error('Error fetching contract:', error);
      throw error;
    }
  }, [contractId, token]);

  // Fetch analysis data - returns analysis JSON, analysisId, and version
  const fetchAnalysis = useCallback(async (): Promise<{
    analysis: ContractAnalysisJSON | null;
    analysisId: string | null;
    version: number;
  }> => {
    if (!contractId || !token) return { analysis: null, analysisId: null, version: 1 };

    try {
      const response = await contractService.getAnalysis(token, contractId);

      // The backend returns { _id, analysisData: {...}, fileName: ..., version: ... }
      // The _id is the analysisId we need for clause decisions
      const analysisId = response?._id || null;
      const version = response?.version || 1;
      const analysisData = response?.analysisData || response;

      let analysis: ContractAnalysisJSON | null = null;
      if (analysisData?.jsonAnalysis) {
        analysis = analysisData.jsonAnalysis;
      } else if (analysisData?.clauses) {
        analysis = analysisData;
      } else if (analysisData?.executive_summary) {
        analysis = analysisData;
      }

      return { analysis, analysisId, version };
    } catch (error) {
      // Analysis might not exist yet, that's ok
      console.log('No analysis found for contract');
      return { analysis: null, analysisId: null, version: 1 };
    }
  }, [contractId, token]);

  // Fetch all analysis versions
  const fetchAnalysisVersions = useCallback(async (): Promise<AnalysisVersionInfo[]> => {
    if (!contractId || !token) return [];
    try {
      return await contractService.getAnalysisVersions(token, contractId);
    } catch (error) {
      console.log('Could not fetch analysis versions:', error);
      return [];
    }
  }, [contractId, token]);

  // Load initial data
  useEffect(() => {
    if (!contractId || !token) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    const loadData = async () => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const [contract, analysisResult, analysisVersions] = await Promise.all([
          fetchContract(),
          fetchAnalysis(),
          fetchAnalysisVersions()
        ]);

        const { analysis, analysisId, version: analysisVersion } = analysisResult;

        // Load saved workflow state from contract if it exists
        const savedWorkflowState = (contract as any)?.workflowState;

        // Determine workflow state - use saved state or calculate from data
        let currentStep = savedWorkflowState?.currentStep || 1;
        let completedSteps: number[] = savedWorkflowState?.completedSteps || [];
        let selectedForNegotiation: string[] = savedWorkflowState?.selectedForNegotiation || [];

        // If no saved state, calculate from data
        if (!savedWorkflowState) {
          if (contract) {
            completedSteps = [1];
            currentStep = 2;

            if (analysis) {
              completedSteps = [1, 2];
              currentStep = 3;
            }
          }
        }

        // Load clause decisions from backend if we have an analysis
        let clauseDecisions: Record<string, ClauseDecision> = {};
        if (analysisId && contractId && token) {
          try {
            const decisions = await contractService.getClauseDecisions(token, contractId, analysisId);
            // Convert array to record
            decisions.forEach((decision: any) => {
              clauseDecisions[decision.clauseReference] = {
                clauseId: decision.clauseReference,
                status: decision.status === 'agreed' ? 'approved' :
                       decision.status === 'compromise' ? 'flagged' : decision.status,
                comment: decision.notes,
                decidedBy: decision.decisionMadeBy,
                decidedAt: new Date(decision.decisionDate),
                proposedChange: undefined,
                includeInNegotiation: selectedForNegotiation.includes(decision.clauseReference)
              };
            });
          } catch (error) {
            console.log('Could not load clause decisions:', error);
          }
        }

        // Update includeInNegotiation for loaded decisions
        Object.keys(clauseDecisions).forEach(clauseId => {
          clauseDecisions[clauseId].includeInNegotiation = selectedForNegotiation.includes(clauseId);
        });

        setState(prev => ({
          ...prev,
          contract,
          analysis,
          analysisId,
          analysisVersion,
          analysisVersions,
          clauseDecisions,
          selectedForNegotiation,
          currentStep,
          completedSteps,
          workflowState: {
            ...initialWorkflowState,
            currentStep,
            completedSteps,
            lastModifiedBy: user?.id || ''
          },
          isLoading: false,
        }));
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to load contract',
          isLoading: false,
        }));
      }
    };

    loadData();
  }, [contractId, token, fetchContract, fetchAnalysis, user?.id]);

  // Set current step
  const setCurrentStep = useCallback((step: number) => {
    setState(prev => {
      // Add current step to completed steps if moving forward
      const newCompletedSteps = step > prev.currentStep && !prev.completedSteps.includes(prev.currentStep)
        ? [...prev.completedSteps, prev.currentStep]
        : prev.completedSteps;

      return { ...prev, currentStep: step, completedSteps: newCompletedSteps };
    });

    // Save workflow state to backend
    if (contractId && token) {
      setState(prev => {
        contractService.updateWorkflowState(token, contractId, {
          currentStep: step,
          completedSteps: prev.completedSteps,
          selectedForNegotiation: prev.selectedForNegotiation
        }).catch(err => console.error('Error saving workflow state:', err));
        return prev;
      });
    }
  }, [contractId, token]);

  // Check if user can proceed to a specific step
  const canProceedToStep = useCallback((stepNumber: number): boolean => {
    // Step 1 always accessible
    if (stepNumber === 1) return true;

    // Step 2 requires contract to exist
    if (stepNumber === 2) return !!state.contract;

    // Step 3 requires analysis to exist
    if (stepNumber === 3) return !!state.analysis;

    // Step 4 is accessible after viewing analysis (don't require all decisions)
    if (stepNumber === 4) return !!state.analysis;

    // Step 5 requires items selected for negotiation OR is accessible from summary
    if (stepNumber === 5) return state.selectedForNegotiation.length > 0 || !!state.analysis;

    return false;
  }, [state.contract, state.analysis, state.selectedForNegotiation]);

  // Save workflow state to backend
  const saveWorkflowState = useCallback(async () => {
    if (!contractId || !token) return;

    try {
      await contractService.updateWorkflowState(token, contractId, {
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        selectedForNegotiation: state.selectedForNegotiation
      });
    } catch (error) {
      console.error('Error saving workflow state:', error);
    }
  }, [contractId, token, state.currentStep, state.completedSteps, state.selectedForNegotiation]);

  // Save progress to backend (called when updates are made)
  const saveProgress = useCallback(async (updates: Partial<UseContractWorkflowState>) => {
    if (!contractId || !token) return;

    setState(prev => ({ ...prev, isSaving: true }));

    try {
      // Save workflow state if step or selections changed
      if (updates.currentStep !== undefined || updates.completedSteps !== undefined || updates.selectedForNegotiation !== undefined) {
        const workflowUpdate = {
          currentStep: updates.currentStep ?? state.currentStep,
          completedSteps: updates.completedSteps ?? state.completedSteps,
          selectedForNegotiation: updates.selectedForNegotiation ?? state.selectedForNegotiation
        };
        await contractService.updateWorkflowState(token, contractId, workflowUpdate);
      }

      setState(prev => ({
        ...prev,
        ...updates,
        workflowState: prev.workflowState ? {
          ...prev.workflowState,
          lastModified: new Date(),
          lastModifiedBy: user?.id || ''
        } : null,
        isSaving: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: 'Failed to save progress',
      }));
    }
  }, [contractId, token, user?.id, state.currentStep, state.completedSteps, state.selectedForNegotiation]);

  // Update a single clause decision
  const updateClauseDecision = useCallback(async (
    clauseId: string,
    decision: Partial<ClauseDecision>
  ) => {
    if (!contractId || !token || !state.analysisId) {
      console.error('Missing required data for clause decision update');
      return;
    }

    const existingDecision = state.clauseDecisions[clauseId];
    const updatedDecision: ClauseDecision = {
      clauseId,
      status: decision.status || existingDecision?.status || 'pending',
      comment: decision.comment ?? existingDecision?.comment,
      decidedBy: user?.id || '',
      decidedAt: new Date(),
      proposedChange: decision.proposedChange ?? existingDecision?.proposedChange,
      includeInNegotiation: decision.includeInNegotiation ?? existingDecision?.includeInNegotiation ?? false,
    };

    const newDecisions = {
      ...state.clauseDecisions,
      [clauseId]: updatedDecision,
    };

    // Update selected for negotiation list
    const newSelectedForNegotiation = updatedDecision.includeInNegotiation
      ? Array.from(new Set([...state.selectedForNegotiation, clauseId]))
      : state.selectedForNegotiation.filter(id => id !== clauseId);

    // Update local state immediately
    setState(prev => ({
      ...prev,
      clauseDecisions: newDecisions,
      selectedForNegotiation: newSelectedForNegotiation
    }));

    // Save clause decision to backend using the ClauseDecision model
    try {
      // Map frontend status to backend status
      const backendStatus = updatedDecision.status === 'approved' ? 'agreed' :
                           updatedDecision.status === 'flagged' ? 'compromise' :
                           updatedDecision.status;

      await contractService.updateClauseDecision(
        token,
        contractId,
        state.analysisId,
        clauseId,
        backendStatus,
        updatedDecision.comment
      );

      // Save workflow state with updated selectedForNegotiation
      await contractService.updateWorkflowState(token, contractId, {
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        selectedForNegotiation: newSelectedForNegotiation
      });
    } catch (error) {
      console.error('Error saving clause decision:', error);
    }
  }, [contractId, token, state.analysisId, state.clauseDecisions, state.selectedForNegotiation, state.currentStep, state.completedSteps, user?.id]);

  // Bulk update multiple clause decisions at once
  const bulkUpdateClauseDecisions = useCallback(async (
    clauseIds: string[],
    status: ClauseDecisionStatus
  ) => {
    if (!contractId || !token || !state.analysisId || clauseIds.length === 0) {
      console.error('Missing required data for bulk clause decision update');
      return;
    }

    // Build all new decisions at once
    const newDecisions = { ...state.clauseDecisions };
    clauseIds.forEach(clauseId => {
      const existingDecision = state.clauseDecisions[clauseId];
      newDecisions[clauseId] = {
        clauseId,
        status,
        comment: existingDecision?.comment,
        decidedBy: user?.id || '',
        decidedAt: new Date(),
        proposedChange: existingDecision?.proposedChange,
        includeInNegotiation: existingDecision?.includeInNegotiation ?? false,
      };
    });

    // Update local state immediately with all changes
    setState(prev => ({
      ...prev,
      clauseDecisions: newDecisions
    }));

    // Save all decisions to backend sequentially to avoid race conditions
    const backendStatus = status === 'approved' ? 'agreed' :
                         status === 'flagged' ? 'compromise' :
                         status;

    try {
      // Process sequentially to avoid backend race conditions
      for (const clauseId of clauseIds) {
        await contractService.updateClauseDecision(
          token,
          contractId,
          state.analysisId,
          clauseId,
          backendStatus,
          newDecisions[clauseId].comment
        );
      }

      // Save workflow state once at the end
      await contractService.updateWorkflowState(token, contractId, {
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        selectedForNegotiation: state.selectedForNegotiation
      });
    } catch (error) {
      console.error('Error saving bulk clause decisions:', error);
    }
  }, [contractId, token, state.analysisId, state.clauseDecisions, state.selectedForNegotiation, state.currentStep, state.completedSteps, user?.id]);

  // Toggle clause for negotiation
  const toggleNegotiationSelection = useCallback((clauseId: string) => {
    const isSelected = state.selectedForNegotiation.includes(clauseId);
    const newSelection = isSelected
      ? state.selectedForNegotiation.filter(id => id !== clauseId)
      : [...state.selectedForNegotiation, clauseId];

    // Also update the clause decision
    const existingDecision = state.clauseDecisions[clauseId];
    const updatedDecisions = {
      ...state.clauseDecisions,
      [clauseId]: {
        ...existingDecision,
        clauseId,
        status: existingDecision?.status || 'pending',
        decidedBy: user?.id || '',
        decidedAt: new Date(),
        includeInNegotiation: !isSelected,
      } as ClauseDecision
    };

    setState(prev => ({
      ...prev,
      selectedForNegotiation: newSelection,
      clauseDecisions: updatedDecisions
    }));

    // Save workflow state to backend
    if (contractId && token) {
      contractService.updateWorkflowState(token, contractId, {
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        selectedForNegotiation: newSelection
      }).catch(err => console.error('Error saving workflow state:', err));
    }
  }, [contractId, token, state.selectedForNegotiation, state.clauseDecisions, state.currentStep, state.completedSteps, user?.id]);

  // Set all selected for negotiation
  const setSelectedForNegotiation = useCallback((clauseIds: string[]) => {
    setState(prev => ({ ...prev, selectedForNegotiation: clauseIds }));

    // Save workflow state to backend
    if (contractId && token) {
      contractService.updateWorkflowState(token, contractId, {
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        selectedForNegotiation: clauseIds
      }).catch(err => console.error('Error saving workflow state:', err));
    }
  }, [contractId, token, state.currentStep, state.completedSteps]);

  // Refresh contract data
  const refreshContract = useCallback(async () => {
    const contract = await fetchContract();
    if (contract) {
      setState(prev => ({ ...prev, contract }));
    }
  }, [fetchContract]);

  // Refresh analysis data
  const refreshAnalysis = useCallback(async () => {
    const { analysis, analysisId } = await fetchAnalysis();
    setState(prev => ({
      ...prev,
      analysis,
      analysisId,
      // Update completed steps if analysis now exists
      completedSteps: analysis
        ? Array.from(new Set([...prev.completedSteps, 1, 2]))
        : prev.completedSteps
    }));
  }, [fetchAnalysis]);

  // Run AI analysis with optional progress callback and model selection
  const runAnalysis = useCallback(async (onProgress?: (progress: AnalysisProgress) => void, forceRefresh: boolean = false, model?: string, provider?: 'anthropic' | 'openai') => {
    if (!contractId || !token) return;

    setState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      // Use the new runAIAnalysis method with progress callback that:
      // 1. Fetches uploaded files
      // 2. Sends to AI (Claude or OpenAI) for multi-pass analysis
      // 3. Reports progress via SSE
      // 4. Saves the analysis results
      await contractService.runAIAnalysis(token, contractId, onProgress, forceRefresh, model, provider);
      await refreshAnalysis();
      // Refetch analysis versions after running new analysis
      const analysisVersions = await fetchAnalysisVersions();
      setState(prev => ({
        ...prev,
        currentStep: 3,
        completedSteps: Array.from(new Set([...prev.completedSteps, 1, 2])),
        analysisVersions,
        isSaving: false
      }));
    } catch (error) {
      console.error('Analysis error:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to run analysis. Please ensure a document has been uploaded.',
        isSaving: false
      }));
    }
  }, [contractId, token, refreshAnalysis, fetchAnalysisVersions]);

  // Get step progress (for clause review)
  const getStepProgress = useCallback(() => {
    const total = state.analysis?.clauses?.length || 0;
    const reviewed = Object.values(state.clauseDecisions).filter(
      d => d.status !== 'pending'
    ).length;
    const percentage = total > 0 ? Math.round((reviewed / total) * 100) : 0;

    return { reviewed, total, percentage };
  }, [state.analysis?.clauses?.length, state.clauseDecisions]);

  // Select a specific analysis version
  const selectAnalysisVersion = useCallback(async (version: number) => {
    if (!contractId || !token) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await contractService.getAnalysisVersion(token, contractId, version);

      const analysisId = response?._id || null;
      const analysisData = response?.analysisData || response;

      let analysis: ContractAnalysisJSON | null = null;
      if (analysisData?.jsonAnalysis) {
        analysis = analysisData.jsonAnalysis;
      } else if (analysisData?.clauses) {
        analysis = analysisData;
      } else if (analysisData?.executive_summary) {
        analysis = analysisData;
      }

      // Load clause decisions for this specific version
      let clauseDecisions: Record<string, ClauseDecision> = {};
      if (analysisId) {
        try {
          const decisions = await contractService.getClauseDecisions(token, contractId, analysisId);
          decisions.forEach((decision: any) => {
            clauseDecisions[decision.clauseReference] = {
              clauseId: decision.clauseReference,
              status: decision.status === 'agreed' ? 'approved' :
                     decision.status === 'compromise' ? 'flagged' : decision.status,
              comment: decision.notes,
              decidedBy: decision.decisionMadeBy,
              decidedAt: new Date(decision.decisionDate),
              proposedChange: undefined,
              includeInNegotiation: state.selectedForNegotiation.includes(decision.clauseReference)
            };
          });
        } catch (error) {
          console.log('Could not load clause decisions for version:', error);
        }
      }

      setState(prev => ({
        ...prev,
        analysis,
        analysisId,
        analysisVersion: version,
        clauseDecisions,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error loading analysis version:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to load analysis version',
        isLoading: false
      }));
    }
  }, [contractId, token, state.selectedForNegotiation]);

  // Complete the review - save all decisions, email, and update status to finalized
  const completeReview = useCallback(async (emailData?: { subject: string; body: string }) => {
    if (!contractId || !token || !state.analysisId) {
      console.error('Missing required data for completing review:', { contractId, hasToken: !!token, analysisId: state.analysisId });
      return;
    }

    // Add step 5 to completed steps if not already there
    const newCompletedSteps = state.completedSteps.includes(5)
      ? state.completedSteps
      : [...state.completedSteps, 5];

    // Update local state
    setState(prev => ({
      ...prev,
      currentStep: 5,
      completedSteps: newCompletedSteps,
      isSaving: true
    }));

    try {
      // SAFETY NET: Save all clause decisions that have been made
      // This ensures no decisions are lost if any previous saves failed
      const decisionsToSave = Object.entries(state.clauseDecisions)
        .filter(([_, decision]) => decision.status !== 'pending');

      console.log(`Saving ${decisionsToSave.length} clause decisions before completing review...`);

      // Save all decisions sequentially to avoid race conditions
      for (const [clauseId, decision] of decisionsToSave) {
        const backendStatus = decision.status === 'approved' ? 'agreed' :
                             decision.status === 'flagged' ? 'compromise' :
                             decision.status;

        try {
          await contractService.updateClauseDecision(
            token,
            contractId,
            state.analysisId,
            clauseId,
            backendStatus,
            decision.comment
          );
        } catch (error) {
          console.error(`Failed to save decision for clause ${clauseId}:`, error);
        }
      }

      // Save workflow state to finalize review
      await contractService.updateWorkflowState(token, contractId, {
        currentStep: 5,
        completedSteps: newCompletedSteps,
        selectedForNegotiation: state.selectedForNegotiation
      });

      // Complete review: save negotiation email and update review status to finalized
      console.log('Completing review with emailData:', emailData ? { subject: emailData.subject, bodyLength: emailData.body.length } : 'No email data');
      await contractService.completeReview(token, contractId, {
        reviewStatus: 'finalized',
        ...(emailData && { negotiationEmail: emailData })
      });

      console.log('Review completed successfully with status set to finalized');
    } catch (error) {
      console.error('Error completing review:', error);
    } finally {
      setState(prev => ({ ...prev, isSaving: false }));
    }
  }, [contractId, token, state.analysisId, state.completedSteps, state.selectedForNegotiation, state.clauseDecisions]);

  return {
    ...state,
    setCurrentStep,
    saveProgress,
    canProceedToStep,
    updateClauseDecision,
    bulkUpdateClauseDecisions,
    toggleNegotiationSelection,
    setSelectedForNegotiation,
    refreshContract,
    refreshAnalysis,
    runAnalysis,
    selectAnalysisVersion,
    getStepProgress,
    completeReview,
  };
}
